import { Tooltip } from 'antd'
import { CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons'
import type { ColumnsType, ColumnType } from 'antd/es/table'
import type { TFunction } from 'i18next'
import type { Approval } from '@/types'
import { FULL_APPROVAL_DATA_COLUMNS } from '@/components/table/fullApprovalDataColumns'

const ACTIONS_COL_PROPS = {
  className: 'leap-table-actions-col',
  onCell: () => ({ className: 'leap-table-actions-cell' }),
} as const

/** Columns shown on the dashboard “Recent approvals” widget (not the full reports grid). */
export const DASHBOARD_APPROVAL_COLUMN_KEYS = [
  'documentNo',
  'type',
  'status',
  'year',
  'postingDate',
  'app',
  'materialCodeNo',
  'customerCode',
  'customerName',
] as const

export function columnsScrollWidth(columns: ColumnsType<unknown>, extra = 48): number {
  const total = columns.reduce(
    (sum, col) => sum + (typeof col.width === 'number' ? col.width : 120),
    0
  )
  return total + extra
}

function translateColumnTitle(t: TFunction, keyStr: string, fallback: string): string {
  const map: Record<string, string> = {
    id: t('ReportsPOD:columns.id', 'ID'),
    documentNo: t('ReportsPOD:columns.documentNo', 'Document No'),
    type: t('ReportsPOD:columns.docType', 'Doc Type'),
    status: t('ReportsPOD:columns.status', 'Status'),
    quantity: t('ReportsPOD:columns.quantity', 'Quantity'),
    postingDate: t('ReportsPOD:columns.postingDate', 'Posting Date'),
    fromLocation: t('ReportsPOD:columns.fromLocation', 'From Location'),
    toLocation: t('ReportsPOD:columns.toLocation', 'To Location'),
    transporterCode: t('ReportsPOD:columns.transporterCode', 'Transporter Code'),
    transporterName: t('ReportsPOD:columns.transporterName', 'Transporter Name'),
    actual_qty: t('PendingPOD:form.actualQty', 'Actual'),
    excess_qty: t('PendingPOD:form.excessShortQty', 'Excess'),
    short_qty: t('PendingPOD:form.short_qty', 'Short'),
    vehicleNo: t('ReportsPOD:details.vehicleNo', 'Vehicle'),
    year: t('ReportsPOD:columns.year', 'Year'),
    app: t('ReportsPOD:columns.app', 'App'),
    materialCodeNo: t('ReportsPOD:columns.material', 'Material'),
    customerCode: t('ReportsPOD:columns.customerCode', 'Cust. Code'),
    customerName: t('ReportsPOD:columns.customerName', 'Customer'),
  }
  return map[keyStr] ?? fallback
}

export function translateApprovalColumns(t: TFunction): ColumnsType<Approval> {
  return FULL_APPROVAL_DATA_COLUMNS.map(col => {
    const keyStr = String(col.key || '')
    const fallback = typeof col.title === 'string' ? col.title : keyStr
    return {
      ...col,
      title: typeof col.title === 'string' ? translateColumnTitle(t, keyStr, fallback) : col.title,
    }
  })
}

export function translateDashboardApprovalColumns(t: TFunction): ColumnsType<Approval> {
  const keys = new Set<string>(DASHBOARD_APPROVAL_COLUMN_KEYS)
  return translateApprovalColumns(t).filter(col => keys.has(String(col.key)))
}

type ViewActionOptions = {
  t: TFunction
  onView: (record: Approval) => void
  label?: string
}

export function buildViewActionColumn({ t, onView, label }: ViewActionOptions): ColumnType<Approval> {
  const viewLabel = label ?? t('ReportsPOD:actions.view', 'View')
  return {
    title: t('ReportsPOD:columns.actions', 'Actions'),
    key: 'actions',
    fixed: 'right',
    width: 72,
    ...ACTIONS_COL_PROPS,
    render: (_: unknown, record: Approval) => (
      <div className="leap-table-actions">
        <Tooltip title={viewLabel}>
          <button
            type="button"
            className="leap-table-action-btn"
            aria-label={viewLabel}
            onClick={e => {
              e.stopPropagation()
              onView(record)
            }}
          >
            <EyeOutlined />
          </button>
        </Tooltip>
      </div>
    ),
  }
}

type OpenActionOptions = {
  t: TFunction
  onOpen: (record: Approval) => void
}

export function buildOpenActionColumn({ t, onOpen }: OpenActionOptions): ColumnType<Approval> {
  const openLabel = t('Common:general.open', 'Open')
  return {
    title: t('ReportsPOD:columns.action', 'Action'),
    key: 'open',
    fixed: 'right',
    width: 72,
    ...ACTIONS_COL_PROPS,
    render: (_: unknown, record: Approval) => (
      <div className="leap-table-actions">
        <Tooltip title={openLabel}>
          <button
            type="button"
            className="leap-table-action-btn"
            aria-label={openLabel}
            onClick={e => {
              e.stopPropagation()
              onOpen(record)
            }}
          >
            <EyeOutlined />
          </button>
        </Tooltip>
      </div>
    ),
  }
}

type PendingActionsOptions = {
  t: TFunction
  onView: (record: Approval) => void
  onApprove: (record: Approval) => void
  onReject: (record: Approval) => void
}

export function buildPendingActionsColumn({
  t,
  onView,
  onApprove,
  onReject,
}: PendingActionsOptions): ColumnType<Approval> {
  const viewLabel = t('PendingPOD:actions.actionView', 'View')
  const approveLabel = t('PendingPOD:actions.actionApprove', 'Approve')
  const rejectLabel = t('PendingPOD:actions.actionReject', 'Reject')

  return {
    title: t('ReportsPOD:columns.actions', 'Actions'),
    key: 'actions',
    fixed: 'right',
    width: 116,
    ...ACTIONS_COL_PROPS,
    render: (_: unknown, record: Approval) => (
      <div className="leap-table-actions">
        <Tooltip title={viewLabel}>
          <button
            type="button"
            className="leap-table-action-btn"
            aria-label={viewLabel}
            onClick={e => {
              e.stopPropagation()
              onView(record)
            }}
          >
            <EyeOutlined />
          </button>
        </Tooltip>
        <Tooltip title={approveLabel}>
          <button
            type="button"
            className="leap-table-action-btn leap-table-action-btn--approve"
            aria-label={approveLabel}
            onClick={e => {
              e.stopPropagation()
              onApprove(record)
            }}
          >
            <CheckOutlined />
          </button>
        </Tooltip>
        <Tooltip title={rejectLabel}>
          <button
            type="button"
            className="leap-table-action-btn leap-table-action-btn--reject"
            aria-label={rejectLabel}
            onClick={e => {
              e.stopPropagation()
              onReject(record)
            }}
          >
            <CloseOutlined />
          </button>
        </Tooltip>
      </div>
    ),
  }
}
